import { useTranslation } from "react-i18next";
import {
  CreateHomeworkAssignmentRequestBody,
  QuestionPropsQuestionType,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "../../../../../core";
import { Input } from "@core/input";
import * as HoverCard from "@radix-ui/react-hover-card";
import { IoInformationCircleOutline, IoAdd, IoRemove } from "react-icons/io5";
import multipleIcon from "@packages/assets/multiple.png";
import optionIcon from "@packages/assets/option.png";
import voteIcon from "@packages/assets/vote.png";
import openEndedIcon from "@packages/assets/open_ended_icon.png";

const questionTypeIcons: Record<string, string> = {
  TEST: multipleIcon,
  TF: voteIcon,
  FB: optionIcon,
  OE: openEndedIcon,
};

const QuestionDistributionForm = ({
  assignmentData,
  onChange,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}) => {
  const { t } = useTranslation();

  const handleQuestionTypeChange = (
    type: QuestionPropsQuestionType,
    field: "count" | "point",
    value: number,
  ) => {
    const finalValue = Math.max(0, value);

    onChange({
      questionTypes: [
        ...assignmentData.questionTypes.filter((q) => q.questionType !== type),
        {
          questionType: type,
          count:
            field === "count"
              ? finalValue
              : Math.max(
                  0,
                  assignmentData.questionTypes.find(
                    (q) => q.questionType === type,
                  )?.count ?? 0,
                ),
          point:
            field === "point"
              ? finalValue
              : (assignmentData.questionTypes.find(
                  (q) => q.questionType === type,
                )?.point ?? 0),
          difficulty: assignmentData.assignmentDifficulty,
        },
      ],
    });
  };

  const handleDifficultyChange = (
    difficulty: CreateHomeworkAssignmentRequestBody["assignmentDifficulty"],
  ) => {
    const updatedData: Partial<CreateHomeworkAssignmentRequestBody> = {
      assignmentDifficulty: difficulty,
      questionTypes: assignmentData.questionTypes.map((qType) => ({
        ...qType,
        difficulty: difficulty,
        count: qType.count ?? 0,
      })),
    };

    onChange(updatedData);
  };

  const getDifficultyConfig = (difficulty: string) => {
    switch (difficulty) {
      case "EASY":
        return {
          color: "green",
          bgSelected:
            "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
          bgUnselected:
            "bg-white dark:bg-dark-600 border-gray-200 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-gray-750",
          textSelected: "text-green-700 dark:text-green-300",
          textUnselected: "text-gray-700 dark:text-gray-300",
          iconSelected: "text-green-500 dark:text-green-400",
        };
      case "MEDIUM":
        return {
          color: "amber",
          bgSelected:
            "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
          bgUnselected:
            "bg-white dark:bg-dark-600 border-gray-200 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-gray-750",
          textSelected: "text-amber-700 dark:text-amber-300",
          textUnselected: "text-gray-700 dark:text-gray-300",
          iconSelected: "text-amber-500 dark:text-amber-400",
        };
      case "HARD":
        return {
          color: "red",
          bgSelected:
            "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
          bgUnselected:
            "bg-white dark:bg-dark-600 border-gray-200 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-gray-750",
          textSelected: "text-red-700 dark:text-red-300",
          textUnselected: "text-gray-700 dark:text-gray-300",
          iconSelected: "text-red-500 dark:text-red-400",
        };
      default:
        return {
          color: "gray",
          bgSelected:
            "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700",
          bgUnselected:
            "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750",
          textSelected: "text-gray-700 dark:text-gray-300",
          textUnselected: "text-gray-700 dark:text-gray-300",
          iconSelected: "text-gray-500 dark:text-gray-400",
        };
    }
  };

  return (
    <div className="space-y-8 mt-8">
      {/* Difficulty Selection */}
      <div className="bg-white dark:bg-dark-500 rounded-2xl p-6 border border-gray-200 dark:border-dark-400">
        <div className="mb-4">
          <Text
            variant="h3"
            className="text-gray-900 dark:text-gray-100 font-semibold"
          >
            {t("assignments.generator.questions.selectDifficulty")}
          </Text>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t("assignments.generator.questions.difficultyDescription")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {["EASY", "MEDIUM", "HARD"].map((difficulty) => {
            const config = getDifficultyConfig(difficulty);
            const isSelected =
              assignmentData.assignmentDifficulty === difficulty;

            return (
              <button
                key={difficulty}
                onClick={() =>
                  handleDifficultyChange(
                    difficulty as CreateHomeworkAssignmentRequestBody["assignmentDifficulty"],
                  )
                }
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200 text-center
                  ${isSelected ? config.bgSelected : config.bgUnselected}
                  ${isSelected ? config.textSelected : config.textUnselected}
                `}
              >
                <div className="font-medium text-sm">
                  {t(
                    `assignments.generator.questions.difficulties.${difficulty.toLowerCase()}`,
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Question Distribution */}
      <div className="bg-white dark:bg-dark-500 rounded-2xl p-6 border border-gray-200 dark:border-dark-400">
        <div className="mb-6">
          <Text
            variant="h3"
            className="text-gray-900 dark:text-gray-100 font-semibold"
          >
            {t("assignments.generator.questions.distribution")}
          </Text>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t("assignments.generator.questions.distributionDescription")}
          </p>
        </div>

        <div className="space-y-4">
          {Object.values(QuestionPropsQuestionType)
            .filter((type) => type !== "MATCH")
            .map((type) => {
              const qType = assignmentData.questionTypes.find(
                (q) => q.questionType === type,
              ) || {
                questionType: type,
                count: 0,
                point: 0,
                difficulty: assignmentData.assignmentDifficulty,
              };

              const currentCount = Math.max(0, qType.count ?? 0);
              const currentPoints = qType.point ?? 0;

              return (
                <div
                  key={type}
                  className="bg-gray-50 dark:bg-dark-600 rounded-xl p-5 border border-gray-100 dark:border-dark-300"
                >
                  <div className="flex items-center justify-between">
                    {/* Question Type Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center p-2">
                        <img
                          src={questionTypeIcons[type]}
                          alt={t(
                            `assignments.generator.questions.types.${type}`,
                          )}
                          className="object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {t(`assignments.generator.questions.types.${type}`)}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {currentCount > 0
                            ? t(
                                "assignments.generator.questions.questionCount",
                                { count: currentCount },
                              )
                            : t("assignments.generator.questions.noQuestions")}
                        </p>
                      </div>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-6">
                      {/* Count Control */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-0">
                          {t("assignments.generator.questions.count")}
                        </label>
                        <div className="flex items-center bg-white dark:bg-dark-500 rounded-lg border border-gray-200 dark:border-dark-400">
                          <button
                            onClick={() =>
                              handleQuestionTypeChange(
                                type,
                                "count",
                                Math.max(0, currentCount - 1),
                              )
                            }
                            disabled={currentCount <= 0}
                            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <IoRemove className="w-4 h-4" />
                          </button>
                          <div className="px-3 py-2 min-w-[3rem] text-center font-medium text-gray-900 dark:text-gray-100">
                            {currentCount}
                          </div>
                          <button
                            onClick={() =>
                              handleQuestionTypeChange(
                                type,
                                "count",
                                currentCount + 1,
                              )
                            }
                            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          >
                            <IoAdd className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Points Control */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("assignments.generator.questions.points")}
                          </label>
                          <HoverCard.Root openDelay={0} closeDelay={0}>
                            <HoverCard.Trigger asChild>
                              <button className="focus:outline-none">
                                <IoInformationCircleOutline className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                              </button>
                            </HoverCard.Trigger>
                            <HoverCard.Portal>
                              <HoverCard.Content
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs border border-gray-200 dark:border-gray-700"
                                side="top"
                                align="center"
                                sideOffset={5}
                              >
                                {t(
                                  "assignments.generator.questions.pointIntroduction",
                                )}
                                <HoverCard.Arrow className="fill-white dark:fill-gray-800" />
                              </HoverCard.Content>
                            </HoverCard.Portal>
                          </HoverCard.Root>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="range"
                            min={0}
                            max={10}
                            value={currentPoints}
                            onChange={(e) =>
                              handleQuestionTypeChange(
                                type,
                                "point",
                                Number(e.target.value),
                              )
                            }
                            classNames={{
                              input:
                                "w-20 h-2 bg-gray-200 dark:bg-dark-600 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500",
                            }}
                          />
                          <div className="min-w-[2rem] text-sm font-medium text-gray-900 dark:text-gray-100 text-center">
                            {currentPoints}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default QuestionDistributionForm;
